/**
 * AssigneeSelector Component
 * Reusable multi-assignee selector with two variants:
 *   - 'chips'  : pill chips with name + X button (for create/edit modals)
 *   - 'stack'  : overlapping avatars with +N overflow (for view contexts)
 *
 * Props:
 *   value        {string[]}  — array of selected assignee IDs
 *   onChange     {fn}        — (ids: string[]) => void
 *   assignees    {object[]}  — available assignees [{ id, name }]
 *   variant      {'chips'|'stack'}
 *   maxAssignees {number}    — default 10
 *   readOnly     {boolean}
 */
import React, { useState, useRef } from 'react';
import {
  Box, Avatar, Chip, Tooltip, Popover, List, ListItem,
  ListItemAvatar, ListItemText, Typography, IconButton, TextField, InputAdornment
} from '@mui/material';
import { Add, Search, Close } from '@mui/icons-material';
import { COLORS } from './colors';
import { CHARACTER_LIMITS } from './appConstants';
import OverflowTooltip from './OverflowTooltip';
import { stringToColor } from './commonFunctions';

const MAX = CHARACTER_LIMITS.MAX_ASSIGNEES || 10;

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || name[0]?.toUpperCase() || '?';
};

const AssigneeAvatar = ({ name, size = 28 }) => (
  <Avatar sx={{ width: size, height: size, fontSize: size * 0.4, bgcolor: stringToColor(name), fontWeight: 600 }}>
    {getInitials(name)}
  </Avatar>
);

const AssigneeSelector = ({
  value = [],
  onChange,
  assignees = [],
  variant = 'chips',
  maxAssignees = MAX,
  readOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);

  const selectedIds = Array.isArray(value) ? value : [];
  const selectedIdStrings = selectedIds.map((id) => String(id));
  const selectedAssignees = selectedIds
    .map(id => assignees.find(a => String(a.id) === String(id)))
    .filter(Boolean);

  const available = assignees.filter(a =>
    !selectedIdStrings.includes(String(a.id)) &&
    a.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = (assignee) => {
    if (selectedIds.length >= maxAssignees) return;
    if (selectedIdStrings.includes(String(assignee.id))) return;
    onChange([...selectedIds, assignee.id]);
  };

  const handleRemove = (id) => {
    if (selectedIds.length <= 1) return; // min 1 assignee
    onChange(selectedIds.filter((i) => String(i) !== String(id)));
  };

  const openPopover = (e) => {
    if (readOnly) return;
    setSearch('');
    setAnchorEl(e.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  // ── CHIPS VARIANT ──────────────────────────────────────────────────────────
  if (variant === 'chips') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
        {selectedAssignees.map(a => (
          <Chip
            key={a.id}
            avatar={<AssigneeAvatar name={a.name} size={22} />}
            label={a.name}
            size="small"
            onDelete={!readOnly && selectedIds.length > 1 ? () => handleRemove(a.id) : undefined}
            deleteIcon={<Close sx={{ fontSize: 14 }} />}
            sx={{
              bgcolor: COLORS.blue?.[50] || '#eff6ff',
              color: COLORS.blue?.[700] || '#1d4ed8',
              border: `1px solid ${COLORS.blue?.[200] || '#bfdbfe'}`,
              fontWeight: 500,
              fontSize: '12px',
              '& .MuiChip-avatar': { width: 22, height: 22 }
            }}
          />
        ))}
        {!readOnly && selectedIds.length < maxAssignees && (
          <>
            <Tooltip title="Add assignee" arrow>
              <IconButton
                ref={triggerRef}
                size="small"
                onClick={openPopover}
                sx={{
                  width: 28, height: 28,
                  border: `1.5px dashed ${COLORS.gray?.[400] || '#9ca3af'}`,
                  color: COLORS.gray?.[500] || '#6b7280',
                  '&:hover': { borderColor: COLORS.primary || '#3b82f6', color: COLORS.primary || '#3b82f6', bgcolor: COLORS.blue?.[50] || '#eff6ff' }
                }}
              >
                <Add sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <AssigneePopover
              anchorEl={anchorEl}
              onClose={closePopover}
              available={available}
              search={search}
              onSearch={setSearch}
              onAdd={handleAdd}
              atLimit={selectedIds.length >= maxAssignees}
            />
          </>
        )}
      </Box>
    );
  }

  // ── STACK VARIANT ──────────────────────────────────────────────────────────
  const MAX_VISIBLE = 3;
  const visible = selectedAssignees.slice(0, MAX_VISIBLE);
  const overflow = selectedAssignees.length - MAX_VISIBLE;
  const tooltipNames = selectedAssignees.map(a => a.name).join(', ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={tooltipNames || 'Unassigned'} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {visible.map((a, i) => (
            <Box
              key={a.id}
              sx={{ ml: i === 0 ? 0 : -0.75, zIndex: MAX_VISIBLE - i, position: 'relative' }}
            >
              <Avatar
                sx={{
                  width: 24, height: 24, fontSize: '10px',
                  bgcolor: stringToColor(a.name),
                  fontWeight: 600,
                  border: `2px solid ${COLORS.white || '#fff'}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                }}
              >
                {getInitials(a.name)}
              </Avatar>
            </Box>
          ))}
          {overflow > 0 && (
            <Box sx={{ ml: -0.75, zIndex: 0, position: 'relative' }}>
              <Avatar
                sx={{
                  width: 24, height: 24, fontSize: '9px',
                  bgcolor: COLORS.gray?.[400] || '#9ca3af',
                  fontWeight: 700,
                  border: `2px solid ${COLORS.white || '#fff'}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                }}
              >
                +{overflow}
              </Avatar>
            </Box>
          )}
          {selectedAssignees.length === 0 && (
            <Avatar sx={{ width: 24, height: 24, fontSize: '10px', bgcolor: COLORS.gray?.[300] || '#d1d5db' }}>
              ?
            </Avatar>
          )}
        </Box>
      </Tooltip>

      {!readOnly && (
        <>
          <Tooltip title="Edit assignees" arrow>
            <IconButton
              size="small"
              onClick={openPopover}
              sx={{
                ml: 0.5, width: 22, height: 22,
                border: `1.5px dashed ${COLORS.gray?.[400] || '#9ca3af'}`,
                color: COLORS.gray?.[500] || '#6b7280',
                '&:hover': { borderColor: COLORS.primary || '#3b82f6', color: COLORS.primary || '#3b82f6' }
              }}
            >
              <Add sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <AssigneePopover
            anchorEl={anchorEl}
            onClose={closePopover}
            available={available}
            search={search}
            onSearch={setSearch}
            onAdd={handleAdd}
            atLimit={selectedIds.length >= maxAssignees}
            selectedAssignees={selectedAssignees}
            onRemove={handleRemove}
            canRemove={selectedIds.length > 1}
          />
        </>
      )}
    </Box>
  );
};

const AssigneePopover = ({
  anchorEl, onClose, available, search, onSearch, onAdd,
  atLimit, selectedAssignees = [], onRemove, canRemove
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    PaperProps={{ sx: { width: 260, maxWidth: 'calc(100vw - 32px)', maxHeight: 360, overflowX: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
  >
    <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.gray?.[100] || '#f3f4f6'}` }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search assignees…"
        value={search}
        onChange={e => onSearch(e.target.value)}
        autoFocus
        InputProps={{
          startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: COLORS.gray?.[400] || '#9ca3af' }} /></InputAdornment>
        }}
        sx={{ '& .MuiInputBase-root': { fontSize: '13px' } }}
      />
    </Box>

    <Box sx={{ px: 1.5, pt: 1, pb: 1, flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden' }}>
      {selectedAssignees.length > 0 && (
        <>
          <Typography variant="caption" sx={{ color: COLORS.gray?.[500] || '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
            Assigned
          </Typography>
          <List dense disablePadding>
            {selectedAssignees.map(a => (
              <ListItem
                key={a.id}
                disablePadding
                sx={{ py: 0.25 }}
                secondaryAction={
                  canRemove && onRemove ? (
                    <IconButton edge="end" size="small" onClick={() => onRemove(a.id)}>
                      <Close sx={{ fontSize: 13, color: COLORS.gray?.[500] || '#6b7280' }} />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <AssigneeAvatar name={a.name} size={26} />
                </ListItemAvatar>
                <ListItemText
                  primary={(
                    <OverflowTooltip
                      title={a.name}
                      tooltipWidth={360}
                      placement="top-start"
                      className="overflow-tooltip-dropdown"
                      sx={{ fontSize: '13px', fontWeight: 500, maxWidth: '100%' }}
                    />
                  )}
                  sx={{ minWidth: 0, overflow: 'hidden' }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {available.length > 0 && (
        <Typography variant="caption" sx={{ color: COLORS.gray?.[500] || '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px', display: 'block', mt: selectedAssignees.length > 0 ? 0.5 : 0 }}>
          {selectedAssignees.length > 0 ? 'Add more' : 'Assignees'}
        </Typography>
      )}
      {atLimit ? (
        <Typography variant="caption" sx={{ color: COLORS.gray?.[500] || '#6b7280', display: 'block', py: 1 }}>
          Maximum {CHARACTER_LIMITS.MAX_ASSIGNEES} assignees reached
        </Typography>
      ) : available.length === 0 && selectedAssignees.length > 0 ? (
        <Typography variant="caption" sx={{ color: COLORS.gray?.[400] || '#9ca3af', display: 'block', py: 1 }}>
          No more assignees available
        </Typography>
      ) : (
        <List dense disablePadding>
          {available.map(a => (
            <ListItem
              key={a.id}
              disablePadding
              button
              onClick={() => { onAdd(a); onSearch(''); }}
              sx={{
                minWidth: 0,
                borderRadius: 1, py: 0.25,
                '&:hover': { bgcolor: COLORS.blue?.[50] || '#eff6ff' }
              }}
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <AssigneeAvatar name={a.name} size={26} />
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <OverflowTooltip
                    title={a.name}
                    tooltipWidth={360}
                    placement="top-start"
                    className="overflow-tooltip-dropdown"
                    sx={{ fontSize: '13px', maxWidth: '100%' }}
                  />
                )}
                sx={{ minWidth: 0, overflow: 'hidden' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  </Popover>
);

export default AssigneeSelector;
